import React, { useEffect, useState } from 'react';

import config from 'config';
import styled, { css } from 'styled-components';

import { Link } from '~/techstyle-shared/next-routes';
import { useReviewableProducts } from '~/techstyle-shared/react-accounts';
import { useBreakpoint, Modal } from '~/techstyle-shared/react-components';
import { FormattedMessage } from '~/techstyle-shared/react-intl';

import AccountBox from '../AccountBox';
import AccountInfoEmptyLayout, {
  AccountInfoEmptyShopNowButton,
} from '../AccountInfoEmptyLayout';
import BasketItem from '../BasketItem';
import MobileStepTitleContainer from '../MobileStepTitleContainer';
import WriteReviewLayout from '../WriteReviewLayout';

const Wrapper = styled(AccountBox)`
  ${({ theme }) => theme.typography.variants.reviewsLayoutText.textStyle}
`;

const ListWrapper = styled.div`
  width: 100%;
`;

const Field = styled.div`
  margin-top: ${({ theme }) => theme.sizes.xs}px;

  > a {
    color: ${({ theme }) => theme.colors.linkText};
    font-size: 14px;
    font-weight: 700;
  }
`;

const dialogStyle = css`
  ${({
    theme: {
      colors,
      spacing: { sizes },
    },
  }) => `
    background-color: ${colors.palette.white};
    height: 100vh;
    position: relative;
    > div:first-of-type {
      position: fixed;
      width: 100%;
      top: 0;
    }
  `}
  width: 100%;
`;

const MobileReviewModal = styled(Modal).attrs({
  dialogStyle,
})`
  margin-top: ${({ theme: { spacing } }) => spacing.sizes.pixels.m * 3}px;
`;

const giftId = config.get('public.productTypes.giftId');
const setId = config.get('public.productTypes.setId');
const excludedProductTypes = [giftId, setId];

export default function ReviewsLayout() {
  const { reviewableProducts } = useReviewableProducts();
  const [mobileReviewProduct, setMobileReviewProduct] = useState(null);
  const [filteredProducts, setFilteredProducts] = useState([]);

  useEffect(() => {
    if (reviewableProducts && reviewableProducts.length > 0) {
      setFilteredProducts(
        reviewableProducts.filter(
          (product) => !excludedProductTypes.includes(product.productTypeId)
        )
      );
    }
  }, [reviewableProducts]);

  const { isMobile } = useBreakpoint();

  if (isMobile && mobileReviewProduct) {
    return (
      <Wrapper>
        <MobileReviewModal
          title="mobile-review-modal"
          isOpen={mobileReviewProduct !== null}
        >
          <MobileStepTitleContainer
            backFunction={() => setMobileReviewProduct(null)}
            exitFunction={() => setMobileReviewProduct(null)}
          >
            <FormattedMessage
              id="site_account_reviews.write_a_review_header"
              defaultMessage="Write a Review"
            />
          </MobileStepTitleContainer>
          <WriteReviewLayout
            initialProduct={mobileReviewProduct}
            handleCancel={() => setMobileReviewProduct(null)}
            filteredProducts={filteredProducts}
            setFilteredProducts={setFilteredProducts}
          />
        </MobileReviewModal>
      </Wrapper>
    );
  }

  if (filteredProducts.length === 0) {
    return (
      <AccountInfoEmptyLayout>
        <FormattedMessage
          id="site_account_reviews.no_reviews"
          defaultMessage="You don't have any items to review"
        />
        <AccountInfoEmptyShopNowButton />
      </AccountInfoEmptyLayout>
    );
  }

  return (
    <Wrapper>
      <ListWrapper>
        {filteredProducts.map((product) => {
          const handleClick = (e) => {
            if (isMobile) {
              e.preventDefault();
              setMobileReviewProduct(product);
            }
          };

          return (
            <BasketItem
              key={product.id}
              product={{
                ...product,
                instanceLabel: product.instanceLabel || product.productSize,
              }}
              pSource="my_account_reviews"
              showQuantitySelector={false}
              showQuantity={false}
              hideDetails={false}
              showStock={false}
              showRemoveIcon={false}
              showPrice={false}
              membership={{}}
              isMobile={isMobile}
              setMobileReviewProduct={setMobileReviewProduct}
              extraDetail={
                <Field>
                  <Link
                    href="/account/write-review/[reviewId]"
                    as={`/account/write-review/${product.id}`}
                    passHref
                  >
                    {/* eslint-disable-next-line */}
                    <a onClick={handleClick} tabIndex={-1}>
                      <FormattedMessage
                        defaultMessage="Write a review"
                        id="site_account_reviews.write_a_review_link"
                      />
                    </a>
                  </Link>
                </Field>
              }
            />
          );
        })}
      </ListWrapper>
    </Wrapper>
  );
}

ReviewsLayout.propTypes = {};
